import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import useSiteConfig from "@/hooks/useSiteConfig";

const links = [
  { label: "Services", href: "/services", external: true },
  { label: "AI Solutions", href: "/#ai-solutions" },
  { label: "Case Studies", href: "/#case-studies" },
  { label: "Process", href: "/#process" },
  { label: "Insights", href: "/#insights" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { config } = useSiteConfig();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "py-3" : "py-5"}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          className={`flex items-center justify-between rounded-full px-5 md:px-6 py-3 transition-all duration-500 ${
            scrolled ? "glass-strong shadow-[0_8px_40px_-12px_rgba(0,0,0,0.6)]" : "bg-transparent"
          }`}
        >
          <a href="#top" className="flex items-center gap-2 group" data-testid="nav-logo">
            <img
              src="/logo.svg"
              alt="Quantum Star Marketing logo"
              className="h-8 w-8 drop-shadow-[0_0_18px_rgba(157,76,221,0.55)]"
            />
            <span className="font-clash text-white text-base md:text-lg font-semibold tracking-tight leading-none">
              Quantum Star
              <span className="text-cyan-400"> Marketing</span>
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm text-zinc-300 hover:text-white transition-colors duration-200 relative group"
              >
                {l.label}
                <span className="absolute left-0 -bottom-1 h-px w-0 bg-gradient-to-r from-cyan-400 to-purple-400 transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href={config.contact.bookingUrl || "#contact"}
              data-testid="nav-cta-book"
              className="hidden md:inline-flex relative items-center gap-2 rounded-full bg-white text-black px-5 py-2.5 text-sm font-medium hover:scale-[1.03] active:scale-[0.98] transition-transform"
            >
              Book Strategy Call
              <span className="text-zinc-500">→</span>
            </a>
            <button
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full glass"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="md:hidden mt-2 glass-strong rounded-2xl p-4 flex flex-col gap-1"
              data-testid="nav-mobile-menu"
            >
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="px-3 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/5"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={config.contact.bookingUrl || "#contact"}
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center justify-center rounded-full bg-white text-black px-5 py-2.5 text-sm font-medium"
              >
                Book Strategy Call
              </a>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}
